import { Injectable, signal } from '@angular/core';
import {
  ActiveLookup,
  Association,
  AssociationKeys,
  ChangeSourceType,
  Lookup,
  LookupKeys,
} from '@ag-common-lib/public-api';
import { AgentAssociationsService } from '../../../../services/agent-associations.service';
import { updateDoc } from 'firebase/firestore';
import { BaseFormService } from '../../../../utils/base-form-service';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';

@Injectable()
export class AssociationFormService extends BaseFormService<Association> {
  selectedGender = signal(null);
  selectedRelationshipType = signal(null);

  constructor(private readonly agentAssociationsService: AgentAssociationsService) {
    super();
  }

  public saveAssociation = (agentId: string, action: ChangeSourceType | null = null) => {
    return this.formData[BaseModelKeys.dbId]
      ? this.updateAssociation(agentId, action)
      : this.createAssociation(agentId, action);
  };

  public getComparator = key => {
    if (key === 'associationTypeRef') {
      return (value, initialValue) => {
        return value?.id === initialValue?.id;
      };
    }

    return undefined;
  };

  public getFormData = (association?: Partial<Association>) => {
    const initialAssociationDietaryConsideration = Object.assign(
      {},
      association?.[AssociationKeys.dietaryConsideration],
    );

    const initialAssociationFlightPreference = Object.assign({}, association?.[AssociationKeys.flightPreference]);
    const initialTShirtSizes = Object.assign({}, association?.[AssociationKeys.tShirtSizes]);

    const associationDietaryConsiderationProxy = new Proxy(initialAssociationDietaryConsideration, {
      set: (target, prop, value, receiver) => {
        const prevValue = target[prop];

        if (value !== prevValue) {
          this.formChangesDetector.handleChange(
            [AssociationKeys.dietaryConsideration, prop].join('.'),
            value,
            prevValue,
          );
          Reflect.set(target, prop, value, receiver);
        }

        return true;
      },
    });

    const associationFlightPreferenceProxy = new Proxy(initialAssociationFlightPreference, {
      set: (target, prop, value, receiver) => {
        const prevValue = target[prop];

        if (value !== prevValue) {
          this.formChangesDetector.handleChange([AssociationKeys.flightPreference, prop].join('.'), value, prevValue);
          Reflect.set(target, prop, value, receiver);
        }

        return true;
      },
    });

    const associationTShirtSizesProxy = new Proxy(initialTShirtSizes, {
      set: (target, prop, value, receiver) => {
        const prevValue = target[prop];

        if (value !== prevValue) {
          this.formChangesDetector.handleChange([AssociationKeys.tShirtSizes, prop].join('.'), value, prevValue);
          Reflect.set(target, prop, value, receiver);
        }

        return true;
      },
    });

    const initialASsociation = Object.assign({}, new Association(), association, {
      [AssociationKeys.dietaryConsideration]: associationDietaryConsiderationProxy,
      [AssociationKeys.tShirtSizes]: associationTShirtSizesProxy,
      [AssociationKeys.flightPreference]: associationFlightPreferenceProxy,
    });

    this.formData = new Proxy(initialASsociation, {
      set: (target, prop, value, receiver) => {
        const prevValue = target[prop];
        const comparator = this.getComparator(prop);
        const isEqualToPrev = comparator ? comparator(value, prevValue) : prevValue === value;

        if (!isEqualToPrev) {
          this.formChangesDetector.handleChange(prop, value, prevValue, comparator);
          Reflect.set(target, prop, value, receiver);
        }

        return true;
      },
    });

    return this.formData;
  };

  setSelectedGender = (item: Lookup) => {
    this.selectedGender.set(item);
  };
  setSelectedRelationshipType = (item: ActiveLookup) => {
    this.selectedRelationshipType.set(item);
  };

  private createAssociation = (agentId: string, action: ChangeSourceType | null = null) => {
    this.startProgress();
    return this.agentAssociationsService
      .create(agentId, this.formData, false, action)
      .then(this.onSuccess)
      .then(() => {
        this.formChangesDetector.clear();
      })
      .finally(() => {
        this.stopProgress();
      });
  };

  private updateAssociation = (agentId: string, action: ChangeSourceType | null = null) => {
    this.startProgress();
    return this.agentAssociationsService
      .update(agentId, this.formData[BaseModelKeys.dbId], this.formData, false, action)
      .then(this.onSuccess)
      .then(() => {
        this.formChangesDetector.clear();
      })
      .finally(() => {
        this.stopProgress();
      });
  };

  private onSuccess = () => {
    const assignedLookups = [this.selectedGender(), this.selectedRelationshipType()];
    const promises = assignedLookups
      .filter(lookup => lookup && !lookup?.isAssigned)
      .map(lookup => updateDoc(lookup?.reference, { [LookupKeys.isAssigned]: true }));

    return Promise.all(promises);
  };
}
